/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListWorkloadIdentitiesRequest,
  ListWorkloadIdentitiesSecurity,
} from "../models/operations/listworkloadidentities.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildWorkloadIdentitiesQuery,
  prefetchWorkloadIdentities,
  queryKeyWorkloadIdentities,
  WorkloadIdentitiesQueryData,
} from "./workloadIdentities.core.js";
export {
  buildWorkloadIdentitiesQuery,
  prefetchWorkloadIdentities,
  queryKeyWorkloadIdentities,
  type WorkloadIdentitiesQueryData,
};

export type WorkloadIdentitiesQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * list workloadIdentities
 *
 * @remarks
 * Read the whole trust policy: every trusted issuer and every admitted subject at the organization tier, plus the selected project's tier when the caller names a project, with the agent each subject resolves to. Requires workload:read.
 */
export function useWorkloadIdentities(
  request?: ListWorkloadIdentitiesRequest | undefined,
  security?: ListWorkloadIdentitiesSecurity | undefined,
  options?: QueryHookOptions<
    WorkloadIdentitiesQueryData,
    WorkloadIdentitiesQueryError
  >,
): UseQueryResult<WorkloadIdentitiesQueryData, WorkloadIdentitiesQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildWorkloadIdentitiesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * list workloadIdentities
 *
 * @remarks
 * Read the whole trust policy: every trusted issuer and every admitted subject at the organization tier, plus the selected project's tier when the caller names a project, with the agent each subject resolves to. Requires workload:read.
 */
export function useWorkloadIdentitiesSuspense(
  request?: ListWorkloadIdentitiesRequest | undefined,
  security?: ListWorkloadIdentitiesSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    WorkloadIdentitiesQueryData,
    WorkloadIdentitiesQueryError
  >,
): UseSuspenseQueryResult<
  WorkloadIdentitiesQueryData,
  WorkloadIdentitiesQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildWorkloadIdentitiesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setWorkloadIdentitiesData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: WorkloadIdentitiesQueryData,
): WorkloadIdentitiesQueryData | undefined {
  const key = queryKeyWorkloadIdentities(...queryKeyBase);

  return client.setQueryData<WorkloadIdentitiesQueryData>(key, data);
}

export function invalidateWorkloadIdentities(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "workloadIdentities", "list", ...queryKeyBase],
  });
}

export function invalidateAllWorkloadIdentities(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "workloadIdentities", "list"],
  });
}
